/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import ajax from 'helpers/ajax'
import { Action, createAction } from 'helpers/createAction'
import { Activity } from 'types/entities/Activity'
import { Package } from 'types/entities/Package'
import {
    BookingActivitiesResponse,
    BookingPackagesResponse,
    BookingPackagesTimeslotsResponse,
} from 'types/responses'
import { ThunkAction } from 'model/store/thunk'
import { RootState } from 'helpers/store'
import { BookingFilter } from 'types/app'
import { PackageItem } from 'types/entities/PackageItem'
import { Company } from 'types/entities/Company'
import { PackagesCart } from 'types/entities/PackagesCart'
import { Payment } from 'types/entities/Payment'
import {
    DEFAULT_BASENAME,
    DEFAULT_HALL_ID,
    HAS_BASENAME,
    SELLER_API_KEY,
} from 'helpers/config'
import Dates from 'utils/Dates'
import { BookingInitCartResponse } from 'types/responses/BookingInitCartResponse'
import { Timeslot } from 'types/entities/Timeslot'
import { VerificationType } from 'types/enums/VerificationType'
import { BookingCustomerFormValues } from 'pages/Front/Booking/Schemas/bookingCustomerFormValues'
import VoucherAction from 'store/shop/VoucherAction'
import { Driver } from 'types/entities/Driver'
import { consoleLog } from 'utils/utility'
import SessionAction from 'store/session/SessionAction'
import { PackageCategory } from 'types/enums/PackageCategory'
import {
    PackagesItemByPerson,
    PackagesItemsResponse,
} from 'types/entities/PackagesItems'
import { Reservation } from 'types/entities/Reservation'
import SellerAction from 'store/seller/SellerAction'
import { VerificationStatusResult } from 'types/responses/VerificationStatusResponse'
import RequestAction from 'store/RequestAction'
import type { Offer } from 'types/entities/Offer'

export type BookingPackagesInput = {
    date: string
    adults: number
    children: number
    activity_ids: number[]
}

export type PackageUpsellItem = {
    package_item_id: number
    upsell_option: number
    quantity: number
}

export type PackageCreditItem = {
    package_item_id: number
    quantity: number
}

export type PackagesTimeSlots = {
    package_id: number
    date: string
    adults: number
    children: number
    upsell: PackageUpsellItem[]
    hours_count?: number
}

export type CategoryTimeSlots = {
    package_category_name: PackageCategory
    date: string
    hall_id: number
    adults: number
    children: number
    activity_ids: number[]
    timeslot_id: number | null
}

export type BookPackageParams = {
    package_id: number
    adults: number
    children: number
    upsell: PackageUpsellItem[]
}

export type BookPackageUpsellParams = {
    timeslot_id: number | null
    package_id: number
    adults: number
    children: number
    upsell: PackageUpsellItem[]
}

export type BookTimeslotParams = {
    package_id: number
    timeslot_id: number | null
    adults: number
    children: number
    upsell: PackageUpsellItem[]
    hours_count?: number
}

export type BookUpsellItemsParams = {
    upsell: PackageUpsellItem[]
    seller_id?: number
}

export type BookUpsellItemsFromReservationParams = {
    upsell: PackageUpsellItem[]
    adults?: number
    children?: number
}

type PackageItemQuantityMap = Record<number, number>

export type BookPackageItemsParams = {
    timeslot_id: number | null
    package_item_ids: Array<number | PackageItemQuantityMap>
    package_category_name: string
    upsell: PackageUpsellItem[]
    credits?: PackageCreditItem[]
    adults?: number
    children?: number
    activity_ids: number[]
    seller_id?: number
}

export type BookCustomTimeslotParams = {
    package_id: number
    timeslot_ids: number[]
    adults: number
    children: number
    upsell: PackageUpsellItem[]
    hours_count?: number
}

export type SetCustomerParams = {
    name: string
    first_name: string
    last_name: string
    email: string
    city: string
    phone: string
    street_address: string
    postal_code: string
}

export type ReservationNote = {
    id: number
    guid: string
    reservation_id: number
    note: string
    creator_id: number | null
    created_at: string
    updated_at: string
    sent_at: string | null
    is_public: boolean
}

export type PackageUpsell = {
    packageItem: PackageItem
    upsell_option: number
    count: number
}

export enum SaferPayErrorNames {
    ActionNotSupported = 'ACTION_NOT_SUPPORTED',
    AliasInvalid = 'ALIAS_INVALID',
    AmountInvalid = 'AMOUNT_INVALID',
    AuthenticationFailed = 'AUTHENTICATION_FAILED',
    BlockedByRiskManagement = 'BLOCKED_BY_RISK_MANAGEMENT',
    CardCheckFailed = 'CARD_CHECK_FAILED',
    CardCvcInvalid = 'CARD_CVC_INVALID',
    CardCvcRequired = 'CARD_CVC_REQUIRED',
    CommunicationFailed = 'COMMUNICATION_FAILED',
    CommunicationTimeout = 'COMMUNICATION_TIMEOUT',
    ConditionNotSatisfied = 'CONDITION_NOT_SATISFIED',
    CurrencyInvalid = 'CURRENCY_INVALID',
    GeneralDeclined = 'GENERAL_DECLINED',
    InternalError = 'INTERNAL_ERROR',
    NoContract = 'NO_CONTRACT',
    NoCreditsAvailable = 'NO_CREDITS_AVAILABLE',
    PayerAuthenticationRequired = 'PAYER_AUTHENTICATION_REQUIRED',
    PaymentMeansInvalid = 'PAYMENTMEANS_INVALID',
    PaymentMeansNotSupported = 'PAYMENTMEANS_NOT_SUPPORTED',
    PermissionDenied = 'PERMISSION_DENIED',
    ThreesAuthenticationFailed = '3DS_AUTHENTICATION_FAILED',
    TokenExpired = 'TOKEN_EXPIRED',
    TokenInvalid = 'TOKEN_INVALID',
    TransactionAborted = 'TRANSACTION_ABORTED',
    TransactionAlreadyCaptured = 'TRANSACTION_ALREADY_CAPTURED',
    TransactionDeclined = 'TRANSACTION_DECLINED',
    TransactionInWrongState = 'TRANSACTION_IN_WRONG_STATE',
    TransactionNotFound = 'TRANSACTION_NOT_FOUND',
    TransactionNotStarted = 'TRANSACTION_NOT_STARTED',
    UnexpectedErrorByAcquirer = 'UNEXPECTED_ERROR_BY_ACQUIRER',
    UpdateCardInformation = 'UPDATE_CARD_INFORMATION',
    ValidationFailed = 'VALIDATION_FAILED',
}

export interface PaymentAssert {
    ResponseHeader: {
        SpecVersion: string
        RequestId: string
    }
    Behavior: string
    ErrorName: SaferPayErrorNames
    ErrorMessage: string
    TransactionId: string
}

class BookingAction {
    static readonly SET_ACTIVITIES = 'booking/setActivities'
    static readonly SET_PACKAGES = 'booking/setPackages'
    static readonly SET_TIMESLOTS = 'booking/setTimeslots'
    static readonly SET_TIMESLOTS_ALLOWED = 'booking/setTimeslotsAllowed'
    static readonly SET_PACKAGE_CART = 'booking/setPackageCart'
    static readonly SET_CUSTOM_TIMESLOT = 'booking/setCustomTimeslot'

    static readonly SET_POSSIBLE_TIMESLOTS = 'booking/setPossibleTimeslots'
    static readonly RESET_CUSTOM_TIMESLOTS = 'booking/resetCustomTimeslots'
    static readonly SET_EMAIL_VERIFIED = 'booking/setEmailVerified'
    static readonly SET_LOGIN_VERIFIED = 'booking/setLoginVerified'
    static readonly SET_DATE_CHOSEN_BY_CUSTOMER =
        'booking/setDateChosenByCustomer'
    static readonly SET_FILTER = 'booking/setFilter'
    static readonly SET_FILTER_ADULTS_COUNT = 'booking/setFilterAdultsCount'
    static readonly SET_FILTER_CHILDREN_COUNT = 'booking/setFilterChildrenCount'
    static readonly SET_FILTER_ACTIVITIES = 'booking/setFilterActivities'
    static readonly SET_PACKAGE = 'booking/setPackage'
    static readonly PUSH_RESERVATION_NOTE = 'booking/pushReservationNote'
    static readonly RESET_RESERVATION_NOTE = 'booking/resetReservationNote'

    static readonly SET_UPSELL = 'booking/setUpsell'
    static readonly PUSH_UPSELL = 'booking/pushUpsell'
    static readonly REMOVE_UPSELL = 'booking/removeUpsell'
    static readonly CLEAR_UPSELLS = 'booking/clearUpsells'
    static readonly RESET_CUSTOMER = 'booking/resetCustomer'
    static readonly SET_CUSTOMER = 'booking/setCustomer'
    static readonly SET_COOKIES = 'booking/setCookies'
    static readonly SET_PACKAGES_CART_PAYMENT = 'booking/setPackagesCartPayment'
    static readonly SET_PACKAGES_CART_PAYMENT_ASSERT =
        'booking/setPackagesCartPaymentAssert'

    static readonly SET_CURRENT_NAVIGATION_STEP =
        'booking/setCurrentNavigationStep'
    static readonly CLEAR_BOOKING_PROCESS_STATE =
        'booking/clearBookingProcessState'

    static readonly SET_SELECTED_TIMESLOT = 'booking/setSelectedTimeslot'
    static readonly SET_AVAILABLE_PACKAGES_ITEMS =
        'booking/setAvailablePackagesItems'
    static readonly CLEAR_AVAILABLE_PACKAGES_ITEMS =
        'booking/clearAvailablePackagesItems'
    static readonly SET_SELECTED_PACKAGE_ITEMS =
        'booking/setSelectedPackageItems'
    static readonly SET_SELECTED_PACKAGE_ITEM = 'booking/setSelectedPackageItem'
    static readonly RESET_SELECTED_PACKAGE_ITEMS =
        'booking/resetSelectedPackageItems'

    static fetchReservationsForDate(
        date: string,
        seller_id: number
    ): ThunkAction<Promise<BookingActivitiesResponse>> {
        return async (_dispatch, getState) => {
            const url = `/online-venue/reservation/seller/list-for-date?seller_id=${encodeURIComponent(
                String(seller_id)
            )}&date=${encodeURIComponent(date)}`
            return (await RequestAction.makeAuthenticatedRequest<BookingActivitiesResponse>(
                getState,
                url,
                undefined,
                'get'
            )) as BookingActivitiesResponse
        }
    }

    static fetchReservationOffer(
        reservation_id: number,
        netto_pdf: boolean
    ): ThunkAction<Promise<{ offer: string; download_as: string }>> {
        return async (_dispatch, getState) => {
            const requestProps = {
                document_type: 'offer',
                is_download: true,
                netto_pdf,
            }
            const response = await RequestAction.makeAuthenticatedRequest<{
                offer: string
                download_as: string
            }>(
                getState,
                `/online-venue/reservation/seller/get_reservation_offer/${reservation_id}`,
                requestProps,
                'post'
            )
            if ((response as any)?.offer)
                return response as { offer: string; download_as: string }
            return Promise.reject(response)
        }
    }

    static fetchSetReservationLeadOrigin(
        reservation_id: number,
        lead_origin: string
    ): ThunkAction<Promise<BookingActivitiesResponse>> {
        return async (_dispatch, getState) => {
            const formData = new FormData()
            formData.append('lead_origin', lead_origin)
            return (await RequestAction.makeAuthenticatedRequest<BookingActivitiesResponse>(
                getState,
                `/online-venue/reservation/seller/lead_origin/${reservation_id}`,
                formData,
                'post'
            )) as BookingActivitiesResponse
        }
    }

    static fetchRemoveReservationLeadOrigin(
        reservation_id: number
    ): ThunkAction<Promise<BookingActivitiesResponse>> {
        return async (_dispatch, getState) => {
            return (await RequestAction.makeAuthenticatedRequest<BookingActivitiesResponse>(
                getState,
                `/online-venue/reservation/seller/lead_origin/${reservation_id}`,
                undefined,
                'delete'
            )) as BookingActivitiesResponse
        }
    }

    static fetchActivities(): ThunkAction<Promise<BookingActivitiesResponse>> {
        return (dispatch) => {
            return ajax
                .get('/booking/activities', {
                    params: { hall_id: DEFAULT_HALL_ID },
                })
                .then((r: BookingActivitiesResponse) => {
                    dispatch(BookingAction.setActivities(r.activities))
                    return r
                })
        }
    }

    static fetchSendSecurityCodeForLogin(
        email: string
    ): (dispatch: (a: any) => void) => Promise<{ success: boolean }> {
        return async () => {
            try {
                const response = (await ajax.post(
                    '/online-venue/reservation/send-login-with-code',
                    {
                        identifier: email,
                        api_key: '777f80c6-6d00-11ee-843d-88d7f67de6c2',
                    }
                )) as { success: boolean }
                if (response?.success) return response
                return Promise.reject('login_not_found')
            } catch (_err) {
                consoleLog('fetchSendSecurityCodeForLogin - reject')
                return Promise.reject('login_not_found')
            }
        }
    }

    static fetchGetLoginWithCode(
        email: string,
        code: string
    ): (dispatch: (a: any) => void) => Promise<Driver> {
        return async (dispatch) => {
            const response = (await ajax.post(
                '/online-venue/reservation/login-with-code',
                {
                    identifier: email,
                    code,
                    api_key: '777f80c6-6d00-11ee-843d-88d7f67de6c2',
                }
            )) as {
                user?: Driver
                reason?: string
                access_token: string
                token_type: string
                expires_at: number
                uid: number
                home_hall_id: number
                watchdogWorking: boolean
            }
            if (response.user) {
                dispatch(SessionAction.setSession(response as any))
                return response.user
            }
            consoleLog('fetchGetLoginWithCode - REJECT')
            return Promise.reject(response.reason)
        }
    }

    static fetchGetSellerLoginWithCode(
        email: string,
        code: string
    ): (dispatch: (a: any) => void) => Promise<Driver> {
        return async (dispatch) => {
            const response = (await ajax.post(
                '/online-venue/reservation/seller/login-with-code',
                {
                    identifier: email,
                    code,
                    api_key: SELLER_API_KEY,
                }
            )) as {
                user?: Driver
                reason?: string
                access_token: string
                token_type: string
                expires_at: number
                uid: number
                home_hall_id: number
                watchdogWorking: boolean
            }
            if (response.user) {
                dispatch(SessionAction.setSession(response as any))
                return response.user
            }
            return Promise.reject(response.reason)
        }
    }

    static fetchPackages(
        data: BookingPackagesInput,
        dateChosen: boolean
    ): ThunkAction<Promise<BookingPackagesResponse>> {
        return (dispatch) => {
            return ajax
                .get('/booking/packages', {
                    params: {
                        hall_id: DEFAULT_HALL_ID,
                        ...data,
                        respectDaysLimit: dateChosen ?? false,
                        test_site: false,
                    },
                })
                .then((r: BookingPackagesResponse) => {
                    dispatch(BookingAction.setPackages(r.packages))
                    dispatch(
                        BookingAction.setTimeslotsAllowed(r.timeslotsAllowed)
                    )
                    return r
                })
        }
    }

    static fetchAllPackages(
        date: string
    ): ThunkAction<Promise<BookingPackagesResponse>> {
        return (dispatch) => {
            return ajax
                .get('/booking/packages', {
                    params: {
                        hall_id: DEFAULT_HALL_ID,
                        date,
                        all_items: true,
                    },
                })
                .then((r: BookingPackagesResponse) => {
                    dispatch(BookingAction.setPackages(r.packages))
                    dispatch(
                        BookingAction.setTimeslotsAllowed(r.timeslotsAllowed)
                    )
                    return r
                })
        }
    }

    static fetchPackagesByLocationParams(
        location: Location,
        filter: BookingFilter,
        dateChosen: boolean = false,
        voucherCodes: string[] = []
    ): ThunkAction<Promise<BookingPackagesResponse> | void> {
        return (dispatch) => {
            const queryParams = new URLSearchParams(location.search)
            const filterParams: any = Object.fromEntries(queryParams.entries())
            if (!filterParams) return

            filterParams.activity_ids =
                filterParams.activity_ids?.split(',') ||
                filter.activity_ids ||
                []
            filterParams.adults = +filterParams.adults || filter.adults || 0
            filterParams.children =
                +filterParams.children || filter.children || 0
            filterParams.date =
                filterParams.date || filter.date || Dates.ymd(new Date())
            filterParams.vouchers = voucherCodes

            return dispatch<any>(
                BookingAction.fetchPackages(filterParams, dateChosen)
            )
        }
    }

    static fetchPackagesForHall(
        date: string
    ): ThunkAction<Promise<BookingPackagesResponse>> {
        return (dispatch) =>
            dispatch(BookingAction.fetchAllPackages(date) as any)
    }

    static fetchPackagesByCategoryAndTimeSlot(
        data: CategoryTimeSlots,
        seller_id?: number
    ): ThunkAction<Promise<PackagesItemsResponse>> {
        return (dispatch) => {
            const { package_category_name } = data
            const category =
                package_category_name === ('main' as any)
                    ? PackageCategory.General
                    : package_category_name
            return ajax
                .get('/booking/package-items', {
                    params: {
                        ...data,
                        package_category_name: category,
                        seller_id,
                    },
                })
                .then((r: PackagesItemsResponse) => {
                    if (r?.packageItems) {
                        dispatch(BookingAction.setAvailablePackagesItems(r))
                        return r
                    }
                    return Promise.reject(r)
                })
        }
    }

    static fetchPackagesByCategoryAndTimeSlotForSeller(
        data: CategoryTimeSlots,
        seller_id?: number
    ): ThunkAction<Promise<PackagesItemsResponse>> {
        return (dispatch, getState) => {
            const requestedCategory = data.package_category_name
            // Snapshot booking filter category at request start. Offer/reservation flows often
            // request a cart category that differs from the default booking filter — that is not
            // stale. Only drop the response if the filter category changed while in flight
            // (user switched package category in booking).
            const categoryWhenStarted = (getState() as RootState).bookingState.bookingProcess.filter
                .category

            return ajax
                .get('/booking/package-items-seller', {
                    params: { ...data, seller_id },
                })
                .then((r: PackagesItemsResponse) => {
                    if (r?.packageItems) {
                        const currentCategory = (getState() as RootState).bookingState.bookingProcess.filter
                            .category

                        if (currentCategory !== categoryWhenStarted) {
                            return Promise.reject({
                                stale: true,
                                requestedCategory,
                                currentCategory,
                                categoryWhenStarted,
                            })
                        }

                        dispatch(BookingAction.setAvailablePackagesItems(r))
                        return r
                    }
                    return Promise.reject(r)
                })
        }
    }

    static fetchCategoryTimeSlots(
        data: CategoryTimeSlots,
        seller_id?: number
    ): ThunkAction<Promise<{ timeslots: Timeslot[] }>> {
        return (dispatch) => {
            const { package_category_name, ...rest } = data as CategoryTimeSlots
            const category =
                package_category_name === ('main' as any)
                    ? PackageCategory.General
                    : package_category_name
            const params = {
                ...rest,
                package_category_name: category,
                adults: data.adults,
                children: data.children,
                seller_id,
            }
            return ajax
                .get('/booking/start-times', {
                    params,
                })
                .then((response: any) => {
                    console.log(response)

                    const flatTimeslots: Timeslot[] =
                        response.timeslots.flatMap(
                            (timeslot_array: { timeslots: any }) =>
                                timeslot_array.timeslots
                        )

                    dispatch(BookingAction.setTimeslots(flatTimeslots))

                    return { timeslots: flatTimeslots }
                })
                .catch((e) => {
                    throw e
                })
        }
    }

    /***
     * @deprecated Use fetchBookPackageNew instead
     */
    static fetchBookPackageWithUpsell(
        data: BookPackageUpsellParams,
        packagesCart: PackagesCart
    ): (dispatch: (a: any) => void) => Promise<{ packageCart: PackagesCart }> {
        return (dispatch) => {
            return ajax
                .post(
                    `/booking/cart/${packagesCart.id}/${packagesCart.token}/book/package`,
                    data
                )
                .then((r: { packageCart: PackagesCart }) => {
                    dispatch(BookingAction.setPackageCart(r.packageCart))
                    return r
                })
        }
    }

    /***
     * @deprecated Use fetchBookPackageNew instead
     */
    static fetchBookPackage(
        data: BookTimeslotParams,
        packagesCart: PackagesCart,
        _origin: string = 'BookingAction'
    ): ThunkAction<Promise<{ packageCart: PackagesCart; timeslot: Timeslot }>> {
        return (dispatch) => {
            return ajax
                .post(
                    `/booking/cart/${packagesCart.id}/${packagesCart.token}/book/package`,
                    data
                )
                .then(
                    (r: { packageCart: PackagesCart; timeslot: Timeslot }) => {
                        dispatch(BookingAction.setPackageCart(r.packageCart))
                        return r
                    }
                )
        }
    }

    static fetchBookPackageNew(
        data: BookPackageItemsParams,
        packagesCart: PackagesCart,
        _origin: string = 'BookingAction'
    ): ThunkAction<Promise<{ packageCart: PackagesCart; timeslot: Timeslot }>> {
        return (dispatch) => {
            const { adults, children, ...rest } = data
            const payload = {
                ...rest,
                adults: typeof adults === 'number' ? adults : 0,
                children: typeof children === 'number' ? children : 0,
            }
            return ajax
                .post(
                    `/booking/cart/${packagesCart.id}/${packagesCart.token}/book/package-items`,
                    payload
                )
                .then(
                    (r: { packageCart: PackagesCart; timeslot: Timeslot }) => {
                        dispatch(BookingAction.setPackageCart(r.packageCart))
                        return r
                    }
                )
        }
    }

    static fetchOfferPackageNew(
        data: BookPackageItemsParams,
        packagesCart: PackagesCart,
        offer: Offer,
        _origin: string = 'BookingAction'
    ): ThunkAction<
        Promise<{ packageCart: PackagesCart; timeslot: Timeslot; offer: Offer }>
    > {
        return (dispatch) => {
            const { adults, children, ...rest } = data
            const payload = {
                ...rest,
                adults: typeof adults === 'number' ? adults : 0,
                children: typeof children === 'number' ? children : 0,
            }
            return ajax
                .post(
                    `/booking/cart/${packagesCart.id}/${packagesCart.token}/offer/${offer.id}/package-items`,
                    payload
                )
                .then(
                    (r: {
                        packageCart: PackagesCart
                        timeslot: Timeslot
                        offer: Offer
                    }) => {
                        dispatch(BookingAction.setPackageCart(r.packageCart))
                        return r
                    }
                )
        }
    }

    static fetchBookUpsells(
        data: BookUpsellItemsFromReservationParams,
        packagesCart: PackagesCart,
        _origin: string = 'BookingAction'
    ): ThunkAction<Promise<{ packageCart: PackagesCart; timeslot: Timeslot }>> {
        return (dispatch) => {
            const { adults, children, ...rest } = data
            const payload = {
                ...rest,
                adults: typeof adults === 'number' ? adults : 0,
                children: typeof children === 'number' ? children : 0,
            }
            return ajax
                .post(
                    `/booking/cart/${packagesCart.id}/${packagesCart.token}/book/upsell-items`,
                    payload
                )
                .then(
                    (r: { packageCart: PackagesCart; timeslot: Timeslot }) => {
                        dispatch(BookingAction.setPackageCart(r.packageCart))
                        return r
                    }
                )
        }
    }

    static fetchBookPackageWithCustomTimeslots(
        data: BookCustomTimeslotParams,
        packagesCart: PackagesCart
    ): ThunkAction<Promise<{ packageCart: PackagesCart; timeslot: Timeslot }>> {
        return (dispatch) => {
            return ajax
                .post(
                    `/booking/cart/${packagesCart.id}/${packagesCart.token}/book/package-custom-slots`,
                    data
                )
                .then(
                    (r: { packageCart: PackagesCart; timeslot: Timeslot }) => {
                        dispatch(BookingAction.setPackageCart(r.packageCart))
                        return r
                    }
                )
        }
    }

    static initCart(
        withCleanup: boolean = true
    ): ThunkAction<Promise<BookingInitCartResponse>> {
        return (dispatch) => {
            return ajax
                .post('/booking/cart/create', { hall_id: DEFAULT_HALL_ID })
                .then((r: BookingInitCartResponse) => {
                    dispatch(BookingAction.setEmailVerified(false))
                    if (withCleanup) {
                        dispatch(VoucherAction.resetVouchers() as any)
                        dispatch(BookingAction.setDateChosenByCustomer(false))
                    }
                    dispatch(BookingAction.setPackageCart(r.packageCart))
                    dispatch<Action<string, Payment | null>>(
                        BookingAction.setPackagesCartPayment(null)
                    )
                    dispatch<Action<string, PaymentAssert | null>>(
                        BookingAction.setPackagesCartPaymentAssert(null)
                    )
                    return r
                })
        }
    }

    static initSellerCart(
        withCleanup: boolean = true,
        seller_id: number
    ): ThunkAction<Promise<BookingInitCartResponse>> {
        return async (dispatch): Promise<BookingInitCartResponse> => {
            const r = await ajax.post('/booking/cart/create', {
                hall_id: DEFAULT_HALL_ID,
                seller_id,
            })
            dispatch(BookingAction.setEmailVerified(false))
            if (withCleanup) {
                dispatch(VoucherAction.resetVouchers() as any)
                dispatch(BookingAction.setDateChosenByCustomer(false))
            }
            dispatch(BookingAction.setPackageCart(r.packageCart))
            dispatch<Action<string, Payment | null>>(
                BookingAction.setPackagesCartPayment(null)
            )
            dispatch<Action<string, PaymentAssert | null>>(
                BookingAction.setPackagesCartPaymentAssert(null)
            )
            return r
        }
    }

    static sendReservationNote(
        packagesCart: PackagesCart,
        note: string,
        replied_note_id?: number | null
    ): ThunkAction<Promise<any>> {
        return async (_dispatch, getState) => {
            const formData = new FormData()
            formData.append('note', note)
            if (replied_note_id) {
                formData.append('replied_note_id', String(replied_note_id))
            }
            return RequestAction.makeAuthenticatedRequest<any>(
                getState,
                `/booking/cart/${packagesCart.id}/${packagesCart.token}/note`,
                formData,
                'post'
            )
        }
    }

    static deleteReservationNote(
        reservation_note_id: number
    ): ThunkAction<Promise<any>> {
        return (dispatch) => {
            return ajax
                .delete(
                    `/admin/reservations/delete-note/${reservation_note_id}`
                )
                .then((r: any) => {
                    dispatch(SellerAction.setReservation(r.reservation) as any)
                    return r
                })
        }
    }

    static resendReservationNote(
        reservation: Reservation,
        reservation_note_id: number
    ): ThunkAction<Promise<{ reservationNote: ReservationNote }>> {
        return async (_dispatch, getState) => {
            const formData = new FormData()
            formData.append('reservation_note_id', String(reservation_note_id))
            return RequestAction.makeAuthenticatedRequest<{
                reservationNote: ReservationNote
            }>(
                getState,
                `/admin/reservations/resend-note/${reservation.id}`,
                formData,
                'post'
            )
        }
    }

    static resetReservationNote() {
        return createAction(BookingAction.RESET_RESERVATION_NOTE, null) as any
    }

    static pushReservationNote(data: ReservationNote) {
        return createAction(BookingAction.PUSH_RESERVATION_NOTE, data)
    }

    static fetchPackageCart(
        packagesCart: PackagesCart
    ): ThunkAction<Promise<{ packageCart: PackagesCart }>> {
        return (dispatch) => {
            return ajax
                .get(`/booking/cart/${packagesCart.id}/${packagesCart.token}`, {
                    params: { hall_id: DEFAULT_HALL_ID },
                })
                .then((r: { packageCart: PackagesCart }) => {
                    dispatch(BookingAction.setPackageCart(r.packageCart))
                    return r
                })
        }
    }

    static fetchVerification(
        data: { value: string; hall_id: number; type: VerificationType },
        packagesCart: PackagesCart
    ): (dispatch: (a: any) => void) => Promise<{
        verification_method: string
        verification_request_id: string
        status: number
    }> {
        return async () => {
            const r = (await ajax.post(
                `/booking/cart/${packagesCart.id}/${packagesCart.token}/verify`,
                data
            )) as {
                verification_status: {
                    verification_method: string
                    verification_request_id: string
                    status: number
                }
            }
            return r.verification_status
        }
    }

    static fetchVerificationForShop(data: {
        value: string
        hall_id: number
        type: VerificationType
    }): (dispatch: (a: any) => void) => Promise<{
        verification_method: string
        verification_request_id: string
        status: number
    }> {
        return async () => {
            const r = (await ajax.post('/shop/verify', data)) as {
                verification_status: {
                    verification_method: string
                    verification_request_id: string
                    status: number
                }
            }
            return r.verification_status
        }
    }

    static fetchVerificationForLogin(data: {
        value: string
        hall_id: number
        type: VerificationType
    }): (dispatch: (a: any) => void) => Promise<any> {
        return async () => {
            return ajax.post('/shop/verify', data)
        }
    }

    static fetchSubmitVerificationCode(
        data: {
            verification_code: string
            request_id: string
            type: VerificationType
        },
        packagesCart: PackagesCart
    ): (dispatch: (a: any) => void) => Promise<VerificationStatusResult> {
        return async () => {
            const r = (await ajax.post(
                `/booking/cart/${packagesCart.id}/${packagesCart.token}/submit-verification-code`,
                data
            )) as VerificationStatusResult
            return r
        }
    }

    static fetchSubmitVerificationCodeForShop(data: {
        verification_code: string
        request_id: string
        type: VerificationType
    }): (dispatch: (a: any) => void) => Promise<{
        type?: VerificationType
        result: boolean
        verification_request_id: string
        company?: Company | null
    }> {
        return async () => {
            const r = (await ajax.post(
                '/shop/submit-verification-code',
                data
            )) as {
                type?: VerificationType
                result: boolean
                verification_request_id: string
                company?: Company | null
            }
            return r
        }
    }

    static fetchEditCustomer(
        data: Company
    ): (dispatch: (a: any) => void) => Promise<{ customer: Company }> {
        return () => {
            return ajax.post(
                '/online-venue/reservation/seller/edit-customer',
                data
            ) as Promise<{ customer: Company }>
        }
    }

    static fetchSetCustomer(
        data: BookingCustomerFormValues,
        packagesCart: PackagesCart | null
    ): (dispatch: (a: any) => void) => Promise<{ customer: Company }> {
        return (dispatch) => {
            return ajax
                .post(
                    `/booking/cart/${packagesCart?.id}/${packagesCart?.token}/set-customer`,
                    data
                )
                .then((r: { customer: Company }) => {
                    dispatch(BookingAction.setCustomer(r.customer))
                    return r
                })
        }
    }

    static fetchSetCustomerByCompanyId(
        company_id: number,
        packagesCart: PackagesCart
    ): (dispatch: (a: any) => void) => Promise<{ customer: Company }> {
        return (dispatch) => {
            return ajax
                .post(
                    `/booking/cart/${packagesCart.id}/${packagesCart.token}/set-customer-by-id`,
                    { company_id }
                )
                .then((r: { customer: Company }) => {
                    dispatch(BookingAction.setCustomer(r.customer))
                    return r
                })
        }
    }

    static fetchPayCart(
        packagesCart: PackagesCart
    ): ThunkAction<Promise<{ redirect: string }>> {
        return async (_dispatch, getState) => {
            const body = {
                return_url:
                    location.protocol +
                    '//' +
                    location.host +
                    (HAS_BASENAME ? '/' + DEFAULT_BASENAME : '') +
                    '/booking/paid',
            }
            const r = await RequestAction.makeAuthenticatedRequest<{
                redirect: string
            }>(
                getState,
                `/booking/cart/${packagesCart.id}/${packagesCart.token}/pay`,
                body,
                'post'
            )
            window.location.href = r.redirect
            return r
        }
    }

    static fetchPayUpsellsCart(
        packagesCart: PackagesCart
    ): ThunkAction<Promise<{ redirect: string }>> {
        return async (_dispatch, getState) => {
            const body = {
                return_url:
                    location.protocol +
                    '//' +
                    location.host +
                    (HAS_BASENAME ? '/' + DEFAULT_BASENAME : '') +
                    '/booking/paid',
            }
            const r = await RequestAction.makeAuthenticatedRequest<{
                redirect: string
            }>(
                getState,
                `/booking/cart/${packagesCart.id}/${packagesCart.token}/pay-upsells`,
                body,
                'post'
            )
            window.location.href = r.redirect
            return r
        }
    }

    static fetchPaidGiftCardCart(
        payment_id: number,
        paymentReference: string
    ): ThunkAction<Promise<{ payment: Payment; assert: any }>> {
        return async (dispatch, getState) => {
            const r = await RequestAction.makeAuthenticatedRequest<{
                payment: Payment
                assert: any
            }>(
                getState,
                `/booking/cart/paid/giftcard/${payment_id}/${paymentReference}`,
                undefined,
                'get'
            )
            dispatch(BookingAction.setPackagesCartPayment(r.payment))
            dispatch(BookingAction.setPackagesCartPaymentAssert(r.assert))
            return r
        }
    }

    static fetchPaidCart(
        payment_id: number,
        paymentReference: string
    ): ThunkAction<Promise<{ payment: Payment; assert: any }>> {
        return async (dispatch, getState) => {
            const r = await RequestAction.makeAuthenticatedRequest<{
                payment: Payment
                assert: any
            }>(
                getState,
                `/booking/cart/paid/${payment_id}/${paymentReference}`,
                undefined,
                'get'
            )
            dispatch(BookingAction.setPackagesCartPayment(r.payment))
            dispatch(BookingAction.setPackagesCartPaymentAssert(r.assert))
            return r
        }
    }

    static fetchPossibleTimeslots(
        data: BookTimeslotParams,
        packagesCart: PackagesCart
    ): ThunkAction<Promise<BookingPackagesTimeslotsResponse>> {
        return () => {
            return ajax.post(
                `/booking/cart/${packagesCart.id}/${packagesCart.token}/book/possible-timeslots`,
                data
            ) as Promise<BookingPackagesTimeslotsResponse>
        }
    }

    static setPossibleTimeslots(timeslots: Timeslot[]) {
        return createAction(BookingAction.SET_POSSIBLE_TIMESLOTS, timeslots)
    }

    static setEmailVerified(verified: boolean) {
        return createAction(BookingAction.SET_EMAIL_VERIFIED, verified)
    }

    static setLoginVerified(verified: boolean) {
        return createAction(BookingAction.SET_LOGIN_VERIFIED, verified)
    }

    static setActivities(activities: Activity[]) {
        return createAction(BookingAction.SET_ACTIVITIES, activities)
    }

    static setPackages(packages: Package[]) {
        return createAction(BookingAction.SET_PACKAGES, packages)
    }

    static setPackageItemsSelectedTimeslot(timeslot: Timeslot) {
        return createAction(BookingAction.SET_SELECTED_TIMESLOT, timeslot)
    }

    static setAvailablePackagesItems(
        packagesItemsResponse: PackagesItemsResponse
    ) {
        return createAction(
            BookingAction.SET_AVAILABLE_PACKAGES_ITEMS,
            packagesItemsResponse
        )
    }

    static clearAvailablePackagesItems() {
        return createAction(BookingAction.CLEAR_AVAILABLE_PACKAGES_ITEMS, null)
    }

    static setSelectedPackageItems(
        packagesItemsByPerson: PackagesItemByPerson[]
    ) {
        return createAction(
            BookingAction.SET_SELECTED_PACKAGE_ITEMS,
            packagesItemsByPerson
        )
    }

    static setSelectedPackageItem(packageItem: PackagesItemByPerson) {
        return createAction(
            BookingAction.SET_SELECTED_PACKAGE_ITEM,
            packageItem
        )
    }

    static resetSelectedPackageItems() {
        return createAction(BookingAction.RESET_SELECTED_PACKAGE_ITEMS, null)
    }

    static setTimeslots(timeslots: Timeslot[]) {
        return createAction(BookingAction.SET_TIMESLOTS, timeslots)
    }

    static setTimeslotsAllowed(timeslotsAllowed: boolean) {
        return createAction(
            BookingAction.SET_TIMESLOTS_ALLOWED,
            timeslotsAllowed
        )
    }

    static setPackageCart(packageCart: PackagesCart | null) {
        return createAction(BookingAction.SET_PACKAGE_CART, packageCart)
    }

    static setFilter(data: BookingFilter) {
        return createAction(BookingAction.SET_FILTER, data)
    }

    static setFilterAdultsCount(adults: number) {
        return createAction(BookingAction.SET_FILTER_ADULTS_COUNT, adults)
    }

    static setFilterChildrenCount(children: number) {
        return createAction(BookingAction.SET_FILTER_CHILDREN_COUNT, children)
    }

    static setDateChosenByCustomer(chosen: boolean) {
        return createAction(BookingAction.SET_DATE_CHOSEN_BY_CUSTOMER, chosen)
    }

    static setPackage(data: Package | null) {
        return createAction(BookingAction.SET_PACKAGE, data)
    }

    static pushUpsell(data: PackageUpsell) {
        return createAction(BookingAction.PUSH_UPSELL, data)
    }

    static setUpsell(data: PackageUpsell) {
        return createAction(BookingAction.SET_UPSELL, data)
    }

    static removeUpsell(data: PackageUpsell) {
        return createAction(BookingAction.REMOVE_UPSELL, data)
    }

    static setCustomer(data: Company | null) {
        return createAction(BookingAction.SET_CUSTOMER, data)
    }

    static resetCustomer() {
        return createAction(BookingAction.RESET_CUSTOMER, null)
    }

    static setCookies(cookies: any) {
        return createAction(BookingAction.SET_COOKIES, cookies)
    }

    static setPackagesCartPayment(payment: Payment | null) {
        return createAction(BookingAction.SET_PACKAGES_CART_PAYMENT, payment)
    }

    static setPackagesCartPaymentAssert(paymentAssert: any | null) {
        return createAction(
            BookingAction.SET_PACKAGES_CART_PAYMENT_ASSERT,
            paymentAssert
        )
    }

    static setCurrentNavigationStep(navigationStep: any) {
        return createAction(
            BookingAction.SET_CURRENT_NAVIGATION_STEP,
            navigationStep
        )
    }

    static clearBookingProcessState() {
        return createAction(BookingAction.CLEAR_BOOKING_PROCESS_STATE, null)
    }

    static clearUpsells() {
        return createAction(BookingAction.CLEAR_UPSELLS, null)
    }

    static setCustomTimeslot(payload: Timeslot) {
        return createAction(BookingAction.SET_CUSTOM_TIMESLOT, payload)
    }

    static resetCustomTimeslot() {
        return createAction(BookingAction.RESET_CUSTOM_TIMESLOTS, null)
    }

    static postInquiryMessage(data: {
        name: string
        last_name: string
        email: string
        phone: string
        subject: string
        message: string
        hall_id: string
    }): ThunkAction<Promise<Response>> {
        return () => {
            return ajax.post('/app/post-inquiry', data) as Promise<Response>
        }
    }

    static fetchCreateInquiry(
        packagesCart: PackagesCart
    ): ThunkAction<Promise<any>> {
        return () => {
            return ajax.post(
                `/booking/cart/${packagesCart.id}/${packagesCart.token}/inquiry`,
                {}
            )
        }
    }

    static confirmBookingByCustomer(data: {
        package_cart_guid: string
        package_cart_token: string
        email: string
    }): ThunkAction<Promise<any>> {
        return () => {
            return ajax.post(
                `/booking/${data.package_cart_guid}/${data.package_cart_token}/confirm-by-customer`,
                {
                    email: btoa(data.email),
                }
            )
        }
    }

    static confirmBookingBySeller(
        packagesCart: PackagesCart,
        seller_id: number
    ): ThunkAction<Promise<any>> {
        return () => {
            return ajax.post(
                `/booking/${packagesCart.guid}/${packagesCart.token}/confirm-by-seller`,
                { seller_id }
            )
        }
    }

    static getReservationStatus(
        package_cart_guid: string,
        package_cart_token: string
    ): ThunkAction<Promise<any>> {
        return () => {
            return ajax.get(
                `/booking/cart/${package_cart_guid}/${package_cart_token}/status`
            )
        }
    }

    static getReservationDrivers(
        package_cart_guid: string,
        package_cart_token: string
    ): ThunkAction<Promise<any>> {
        return () => {
            return ajax.get(
                `/booking/cart/${package_cart_guid}/${package_cart_token}/drivers`
            )
        }
    }

    static setReservationDrivers(
        data: any,
        package_cart_guid: string,
        package_cart_token: string
    ): ThunkAction<Promise<any>> {
        return () => {
            return ajax.post(
                `/booking/cart/${package_cart_guid}/${package_cart_token}/set-drivers`,
                data
            )
        }
    }
    static fetchUpdatePackageCartItem(
        packagesCart: PackagesCart,
        cartItemId: number,
        data: { custom_text?: string }
    ): ThunkAction<Promise<any>> {
        return async (dispatch) => {
            return ajax
                .post(
                    `/booking/cart/${packagesCart.id}/${packagesCart.token}/items/${cartItemId}`,
                    data
                )
                .then((r: any) => {
                    if (r.packageCart) {
                        dispatch(BookingAction.setPackageCart(r.packageCart))
                    }
                    return r
                })
        }
    }
}

export default BookingAction
